import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type JobKind,
  type JobRequest,
  type JobResponse,
  jobSchema,
  type NewJobRequest,
  newJobSchema,
  type ProjectAreaChoice,
} from '@k-management/shared';
import { useEffect, useRef, useState } from 'react';
import { Field } from '../../components/Field.js';
import { apiRequest } from '../../lib/api.js';
import { type FieldErrors, showServerError, validate } from '../../lib/form-validation.js';
import { ClientPicker } from '../quotes/ClientPicker.js';
import { jobKeys } from './jobs-ui.js';

interface Form {
  clientId: string | null;
  billingClientId: string | null;
  name: string;
  description: string;
  startOn: string;
  endOn: string;
  notes: string;
  areaCode: string;
  objective: string;
}

/**
 * A new commessa or progetto, a new sottocommessa of `parent` (17/10/2026), or
 * the change of one: kind and client stay once created.
 */
export function JobDialog({
  job,
  kind,
  parent = null,
  onClose,
  onSaved,
}: {
  job: JobResponse | null;
  kind: JobKind;
  parent?: JobResponse | null;
  onClose: () => void;
  onSaved?: (saved: JobResponse) => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [form, setForm] = useState<Form>({
    clientId: job?.clientId ?? null,
    billingClientId: job?.billingClient?.id ?? null,
    name: job?.name ?? '',
    description: job?.description ?? '',
    startOn: job?.startOn ?? '',
    endOn: job?.endOn ?? '',
    notes: job?.notes ?? '',
    areaCode: job?.areaCode ?? '',
    objective: job?.objective ?? '',
  });
  // «Si fattura a» another subject: kept apart from the subject, which is empty while choosing.
  const [ownBilling, setOwnBilling] = useState(Boolean(job?.billingClient));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState('');
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  // The areas where the user may put a progetto (ADR-025).
  const areas = useQuery({
    queryKey: ['jobs', 'area-choices'],
    queryFn: () => apiRequest<ProjectAreaChoice[]>('/project-areas/choices'),
    enabled: kind === 'progetto' && !parent,
  });
  const text = (key: Exclude<keyof Form, 'clientId' | 'billingClientId'>) => ({
    value: form[key],
    onChange: (event: { target: { value: string } }) =>
      setForm({ ...form, [key]: event.target.value }),
  });
  const fields = (): JobRequest => ({
    name: form.name,
    description: form.description,
    startOn: form.startOn || null,
    endOn: form.endOn || null,
    notes: form.notes,
    billingClientId: isCommessa && ownBilling ? form.billingClientId : null,
    areaCode: isCommessa ? null : form.areaCode || null,
    objective: isCommessa ? null : form.objective,
  });
  const save = useMutation({
    mutationFn: (body: JobRequest | NewJobRequest) =>
      job
        ? apiRequest<JobResponse>(`/jobs/${job.id}`, { method: 'PUT', body })
        : parent
          ? apiRequest<JobResponse>(`/jobs/${parent.id}/children`, { method: 'POST', body })
          : apiRequest<JobResponse>('/jobs', { method: 'POST', body }),
    onError: (error) => showServerError(error, setErrors, setFormError),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: jobKeys.all });
      onSaved?.(saved);
      onClose();
    },
  });
  const isCommessa = kind === 'commessa';
  const title = job
    ? `${job.code} · ${job.name}`
    : parent
      ? isCommessa
        ? `Nuova sottocommessa di ${parent.code}`
        : `Nuovo sottoprogetto di ${parent.code}`
      : isCommessa
        ? 'Nuova commessa'
        : 'Nuovo progetto';

  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <div className="stack">
        <h2>{title}</h2>
        {parent && !isCommessa && (
          <p className="muted small">
            Codice {parent.code}-NN, stessa area ({parent.areaName ?? 'senza area'}) e stesso PM del
            progetto, che poi si può cambiare. I costi stanno nei sottoprogetti: il progetto ne è la
            somma.
          </p>
        )}
        {parent && isCommessa && (
          <p className="muted small">
            Codice {parent.code}-NN, stesso cliente ({parent.clientName}) e stesse figure della
            madre, che poi si possono cambiare.
            {parent.childCount === 0 &&
              parent.offerCount > 0 &&
              ' È la prima: le offerte della madre passano a questa sottocommessa, perché la madre diventa la somma delle sue sottocommesse.'}
          </p>
        )}
        {!job && !parent && (
          <p className="muted small">
            {isCommessa
              ? 'Il codice (COM-) si assegna da solo. Le offerte accettate si collegano dalla scheda.'
              : 'Un progetto è interno: niente cliente né offerte, solo costi. Il codice (PRJ-) si assegna da solo; PM, costi e indicatori si aggiungono nella scheda.'}
          </p>
        )}
        {isCommessa && !job && !parent && (
          <ClientPicker
            value={form.clientId}
            onChange={(clientId) => setForm({ ...form, clientId })}
            error={errors.clientId}
            returnTo="/commesse"
          />
        )}
        {isCommessa && (
          <>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={!ownBilling}
                onChange={(event) => setOwnBilling(!event.target.checked)}
              />{' '}
              Si fattura al cliente della commessa
            </label>
            {ownBilling && (
              <ClientPicker
                label="Si fattura a"
                value={form.billingClientId}
                onChange={(billingClientId) => setForm({ ...form, billingClientId })}
                error={errors.billingClientId}
                hint="Il soggetto a cui vanno le fatture, se diverso dal cliente: va scelto tra i Clienti."
                returnTo="/commesse"
              />
            )}
          </>
        )}
        <Field label={isCommessa ? 'Nome' : 'Titolo'} error={errors.name}>
          <input {...text('name')} maxLength={200} />
        </Field>
        <Field label="Descrizione" error={errors.description}>
          <textarea rows={3} {...text('description')} />
        </Field>
        {!isCommessa && !parent && !job?.parentId && (
          <Field
            label="Area"
            error={errors.areaCode}
            hint={
              areas.data?.length === 0
                ? 'Nessuna area: le aggiunge chi gestisce Tabelle › Aree dei progetti.'
                : 'Chi vede e modifica i progetti dipende dall’area.'
            }
          >
            <select {...text('areaCode')}>
              <option value="">—</option>
              {areas.data?.map((area) => (
                <option key={area.code} value={area.code}>
                  {area.name}
                </option>
              ))}
              {job?.areaCode && !areas.data?.some((area) => area.code === job.areaCode) && (
                <option value={job.areaCode}>{job.areaName ?? job.areaCode}</option>
              )}
            </select>
          </Field>
        )}
        {!isCommessa && (
          <Field
            label="Obiettivo da raggiungere"
            error={errors.objective}
            hint="Gli indicatori misurabili si aggiungono nella scheda, uno o più."
          >
            <textarea rows={2} {...text('objective')} />
          </Field>
        )}
        <div className="form-grid">
          <Field label="Inizio previsto" error={errors.startOn}>
            <input type="date" {...text('startOn')} />
          </Field>
          <Field label="Fine prevista" error={errors.endOn}>
            <input type="date" {...text('endOn')} />
          </Field>
        </div>
        <Field label="Note" error={errors.notes}>
          <textarea rows={2} {...text('notes')} />
        </Field>
        {formError && <p className="error">{formError}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={
              save.isPending ||
              !form.name.trim() ||
              (isCommessa && ownBilling && !form.billingClientId)
            }
            onClick={() => {
              const body: JobRequest | NewJobRequest =
                job || parent
                  ? fields()
                  : { kind, clientId: isCommessa ? form.clientId : null, ...fields() };
              const checked =
                job || parent ? validate(jobSchema, body) : validate(newJobSchema, body);
              setErrors(checked.errors);
              setFormError(
                Object.keys(checked.errors).length ? 'Correggi i campi segnati in rosso.' : '',
              );
              if (!Object.keys(checked.errors).length) save.mutate(body);
            }}
          >
            {save.isPending ? 'Attendere…' : job ? 'Salva' : 'Crea'}
          </button>
        </div>
      </div>
    </dialog>
  );
}
